import type { SkillFile, SkillListItem, SkillScope } from "@/types/command-skill";

export const SKILL_SECTION_ORDER: SkillScope[] = [
  "PERSONAL",
  "SHARED",
  "ORGANIZATION",
  "GLOBAL",
];

export const SKILL_SECTION_TITLE: Record<SkillScope, string> = {
  PERSONAL: "My skills",
  SHARED: "Shared with me",
  ORGANIZATION: "Organization",
  GLOBAL: "AIOS",
};

export const SKILL_NAME_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const SKILL_UPLOAD_MAX_BYTES = 30 * 1024 * 1024;
export const SKILL_DESCRIPTION_MAX = 1024;
export const SKILL_INSTRUCTIONS_MAX = 100_000;
export const SKILL_NAME_MAX = 64;

const TRUST_NOTE_KEY = "command-skill-trust-noted";

export function slugifySkillName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, SKILL_NAME_MAX);
}

export function validateSkillName(name: string): string | null {
  if (!name) return "Name is required";
  if (name.length > SKILL_NAME_MAX) return "Name must be 64 characters or fewer";
  if (!SKILL_NAME_PATTERN.test(name)) {
    return "Use lowercase letters, numbers, and single hyphens";
  }
  return null;
}

export function validateSkillDescription(value: string): string | null {
  if (!value.trim()) return "Description is required";
  if (value.length > SKILL_DESCRIPTION_MAX) {
    return "Description must be 1,024 characters or fewer";
  }
  if (/[<>]/.test(value)) return "Description cannot include < or >";
  return null;
}

export function validateSkillInstructions(value: string): string | null {
  if (!value.trim()) return "Instructions are required";
  if (value.length > SKILL_INSTRUCTIONS_MAX) {
    return "Instructions must be 100,000 characters or fewer";
  }
  return null;
}

export function sanitizeDescription(value: string): string {
  return value.replace(/[<>]/g, "").slice(0, SKILL_DESCRIPTION_MAX);
}

export function ownerLabel(skill: Pick<SkillListItem, "is_global" | "is_owner" | "owner">): string {
  if (skill.is_global) return "AIOS";
  if (skill.is_owner) return "You";
  return skill.owner?.fullname?.trim() || "Former member";
}

export function skillMetaLine(skill: SkillListItem): string {
  if (skill.is_global) return "AIOS";
  if (skill.scope === "SHARED") return `Shared by ${ownerLabel(skill)}`;
  if (skill.scope === "ORGANIZATION") return `by ${ownerLabel(skill)}`;
  if (skill.is_owner) return "Created by You";
  return `by ${ownerLabel(skill)}`;
}

export interface SkillBadge {
  key: string;
  label: string;
}

export function skillBadges(skill: SkillListItem, isSuperAdmin: boolean): SkillBadge[] {
  const badges: SkillBadge[] = [];
  if (skill.is_global) {
    if (isSuperAdmin) {
      badges.push({
        key: "lifecycle",
        label: skill.visibility === "TEAM" ? "Live" : "Draft",
      });
    } else {
      badges.push({ key: "aios", label: "AIOS" });
    }
  } else if (skill.visibility === "TEAM" && skill.scope === "PERSONAL") {
    badges.push({ key: "published", label: "Published" });
  }
  if (skill.scope === "SHARED") {
    badges.push({
      key: "shared",
      label: `Shared by ${skill.owner?.fullname?.trim() || "Former member"}`,
    });
  }
  if (skill.scope === "ORGANIZATION" && !skill.is_global) {
    badges.push({
      key: "by",
      label: `by ${skill.owner?.fullname?.trim() || "Former member"}`,
    });
  }
  return badges;
}

export function formatSkillUpdated(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(Math.abs(diffMs) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return days === 1 ? "1 day ago" : `${days} days ago`;
  return date.toLocaleDateString();
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateSkillUpload(file: File, mode: "create" | "version"): string | null {
  const name = file.name.toLowerCase();
  const allowed = mode === "create" ? [".zip", ".skill", ".md"] : [".zip", ".skill"];
  if (!allowed.some((ext) => name.endsWith(ext))) {
    return mode === "create"
      ? "Upload a .zip, .skill, or .md file."
      : "Upload a .zip or .skill file. The skill name must stay the same.";
  }
  if (file.size > SKILL_UPLOAD_MAX_BYTES) return "File must be 30 MB or smaller.";
  return null;
}

export function validateSkillFilePath(path: string, existing: { path: string }[] = []): string | null {
  const value = path.trim();
  if (!value) return "Enter a file path";
  if (value.length > 255) return "Path must be 255 characters or fewer";
  if (value.startsWith("/") || value.includes("\\")) return "Use a relative path without a leading slash";
  const parts = value.split("/");
  if (parts.some((part) => part === "" || part === "." || part === "..")) {
    return "Path cannot include empty segments or ..";
  }
  if (value === "SKILL.md") return "SKILL.md stays with the skill. Edit it from the instructions.";
  const safeExisting = Array.isArray(existing) ? existing : [];
  if (safeExisting.some((file) => file.path.startsWith(`${value}/`))) {
    return `"${value}" is already a folder`;
  }
  for (let i = 1; i < parts.length; i += 1) {
    const prefix = parts.slice(0, i).join("/");
    if (safeExisting.some((file) => file.path === prefix)) {
      return `"${prefix}" is a file, so it cannot contain another file`;
    }
  }
  return null;
}

export const STANDARD_SKILL_FOLDERS = ["references", "scripts", "assets"] as const;

export function skillReadLine(path: string) {
  return `For more detail, read ${path}.`;
}

/** The agent only opens extra files when SKILL.md tells it to. */
export function withSkillReadLine(instructions: string, path: string) {
  if (!path || instructions.includes(path)) return instructions;
  const top = path.split("/")[0];
  if ((STANDARD_SKILL_FOLDERS as readonly string[]).includes(top)) return instructions;
  const line = skillReadLine(path);
  const base = instructions.trimEnd();
  return base ? `${base}\n\n${line}\n` : `${line}\n`;
}

export function skillFolderChoices(files: { path: string }[] = []): string[] {
  const folders = new Set<string>(STANDARD_SKILL_FOLDERS);
  const safeFiles = Array.isArray(files) ? files : [];
  for (const file of safeFiles) {
    if (!file?.path) continue;
    const parts = file.path.split("/");
    for (let i = 1; i < parts.length; i += 1) {
      folders.add(parts.slice(0, i).join("/"));
    }
  }
  return [...folders].sort((a, b) => a.localeCompare(b));
}

export function fileNameFromUpload(name: string): string {
  const base = name.split(/[/\\]/).pop() ?? "file";
  return base.replace(/\.\./g, "").replace(/^\/+/, "");
}

export type SkillTreeNode =
  | { type: "file"; name: string; path: string; file: SkillFile }
  | { type: "folder"; name: string; path: string; children: SkillTreeNode[] };

export function buildSkillTree(files: SkillFile[] = []): SkillTreeNode[] {
  const root: SkillTreeNode[] = [];
  const folders = new Map<string, SkillTreeNode & { type: "folder" }>();
  const safeFiles = Array.isArray(files) ? files : [];

  const sorted = [...safeFiles].sort((a, b) => (a.path || "").localeCompare(b.path || ""));
  for (const file of sorted) {
    if (!file?.path) continue;
    const parts = file.path.split("/");
    let siblings = root;
    let acc = "";
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      const isFile = i === parts.length - 1;
      acc = acc ? `${acc}/${part}` : part;
      if (isFile) {
        siblings.push({ type: "file", name: part, path: file.path, file });
        continue;
      }
      let folder = folders.get(acc);
      if (!folder) {
        folder = { type: "folder", name: part, path: acc, children: [] };
        folders.set(acc, folder);
        siblings.push(folder);
      }
      siblings = folder.children;
    }
  }

  const sortNodes = (nodes: SkillTreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.type === "file" && a.path === "SKILL.md") return -1;
      if (b.type === "file" && b.path === "SKILL.md") return 1;
      if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    for (const node of nodes) {
      if (node.type === "folder") sortNodes(node.children);
    }
  };
  sortNodes(root);
  return root;
}

export function pickSkillForAgent(items: SkillListItem[], name: string): SkillListItem | null {
  const matches = items.filter((item) => item.name === name);
  if (matches.length === 0) return null;
  const enabled = matches.filter((item) => item.enabled);
  const pool = enabled.length > 0 ? enabled : matches;
  return [...pool].sort(
    (a, b) => SKILL_SECTION_ORDER.indexOf(a.scope) - SKILL_SECTION_ORDER.indexOf(b.scope),
  )[0];
}

export function hasTrustNote(skillId: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(TRUST_NOTE_KEY);
    const ids = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(ids) && ids.includes(skillId);
  } catch {
    return false;
  }
}

export function markTrustNote(skillId: string) {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(TRUST_NOTE_KEY);
    const ids = raw ? (JSON.parse(raw) as unknown) : [];
    const list = Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
    if (!list.includes(skillId)) {
      list.push(skillId);
      window.localStorage.setItem(TRUST_NOTE_KEY, JSON.stringify(list.slice(-200)));
    }
  } catch {
    // Storage can be unavailable in private modes.
  }
}

export function codeLanguageForPath(path: string): string | null {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  const languages: Record<string, string> = {
    py: "python",
    js: "javascript",
    jsx: "jsx",
    ts: "typescript",
    tsx: "tsx",
    sh: "bash",
    bash: "bash",
    json: "json",
    yml: "yaml",
    yaml: "yaml",
    css: "css",
    html: "markup",
    xml: "markup",
    sql: "sql",
    csv: "csv",
    rb: "ruby",
    go: "go",
    rs: "rust",
    md: "markdown",
  };
  return languages[ext] ?? null;
}

export function isMarkdownPath(path: string): boolean {
  return path.toLowerCase().endsWith(".md");
}
